import React from "react";
import { useTranslation } from "react-i18next";
import {
  CaseTab,
  DebtorTab,
  CreditorTab,
  InvoiceTab,
} from "components/MainSearch/Components";
import { MainCriterias } from "constants/globalsearch";
import { $Tabs } from "us.common/components";
import _ from "lodash";
import { TabResultsType } from "../Types";

/**
 * @description - Tab container component for control tabs.
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 12/01/2025
 */
export const TabResults: React.FC<TabResultsType> = (props) => {
  const { t } = useTranslation();
  const {
    selectedTab,
    tabInfo,
    invoices,
    cases,
    debtors,
    creditors,
    isDataArchived,
    isInvoiceLoading,
    isCaseLoading,
    isDebtorLoading,
    isCreditorLoading,
    totalInvoiceSearchResult = 0,
    totalCaseSearchResult = 0,
    totalDebtorSearchResult = 0,
    totalCreditorSearchResult = 0,
    tabChangeEvent,
    paginationChangeEvent,
    aRClickEvent,
    changeSortOrderEvent,
  } = props;

  const tabItems = [
    tabInfo.invoiceTab.isTabVisible && {
      label: `${t("US.COLLECTION.COMMON:COMMON.INVOICE")} (${invoices.length})`,
      key: MainCriterias.INVOICE,
      children: selectedTab === MainCriterias.INVOICE && (
        <InvoiceTab
          {..._.pick(props, ["history", "location", "match"])}
          totalInvoiceSearchResult={totalInvoiceSearchResult}
          tabInfo={tabInfo}
          changeSortOrderEvent={changeSortOrderEvent}
          currentHit={tabInfo.invoiceTab.hitValue}
          invoiceData={invoices}
          isDataArchived={isDataArchived}
          isLoading={isInvoiceLoading}
          paginationChangeEvent={(direction) =>
            paginationChangeEvent(direction, MainCriterias.INVOICE)
          }
        />
      ),
    },
    tabInfo.caseTab.isTabVisible && {
      label: `${t("US.COLLECTION.COMMON:COMMON.CASE")} (${cases.length})`,
      key: MainCriterias.CASE,
      children: selectedTab === MainCriterias.CASE && (
        <CaseTab
          {..._.pick(props, ["history", "location", "match"])}
          children={props.children}
          tabInfo={tabInfo}
          totalCaseSearchResult={totalCaseSearchResult}
          caseData={cases}
          isDataArchived={isDataArchived}
          currentHit={tabInfo.caseTab.hitValue}
          isLoading={isCaseLoading}
          changeSortOrderEvent={changeSortOrderEvent}
          paginationChangeEvent={(direction) =>
            paginationChangeEvent(direction, MainCriterias.CASE)
          }
        />
      ),
    },
    tabInfo.debtorTab.isTabVisible &&
      !isDataArchived && {
        label: `${t("US.COLLECTION.COMMON:COMMON.DEBTOR")} (${debtors.length})`,
        key: MainCriterias.DEBTOR,
        children: selectedTab === MainCriterias.DEBTOR && (
          <DebtorTab
            debtorData={debtors}
            currentHit={tabInfo.debtorTab.hitValue}
            isLoading={isDebtorLoading}
            changeSortOrderEvent={changeSortOrderEvent}
            tabInfo={tabInfo}
            totalDebtorSearchResult={totalDebtorSearchResult}
            paginationChangeEvent={(direction) =>
              paginationChangeEvent(direction, MainCriterias.DEBTOR)
            }
            aRClickEvent={aRClickEvent}
          />
        ),
      },
    tabInfo.creditorTab.isTabVisible &&
      !isDataArchived && {
        label: `${t("US.COLLECTION.COMMON:COMMON.CREDITOR")} (${
          creditors.length
        })`,
        key: MainCriterias.CREDITOR,
        children: selectedTab === MainCriterias.CREDITOR && (
          <CreditorTab
            creditorData={creditors}
            currentHit={tabInfo.creditorTab.hitValue}
            isLoading={isCreditorLoading}
            changeSortOrderEvent={changeSortOrderEvent}
            tabInfo={tabInfo}
            totalCreditorSearchResult={totalCreditorSearchResult}
            paginationChangeEvent={(direction) =>
              paginationChangeEvent(direction, MainCriterias.CREDITOR)
            }
          />
        ),
      },
  ].filter(Boolean) as {
    label: string;
    key: MainCriterias;
    children: React.ReactNode;
  }[];

  return (
    <$Tabs
      className="bui-tabs"
      defaultActiveKey={MainCriterias.INVOICE}
      activeKey={selectedTab}
      onTabClick={(activeKey: string) => {
        tabChangeEvent(activeKey as MainCriterias);
      }}
      items={tabItems}
    />
  );
};
